import { useState, useEffect } from "react";
import { Routes, Route, Link, useLocation, useNavigate } from "react-router-dom";
import { User, Package, MapPin, Heart, LogOut, ChevronRight, Trash2, ShoppingCart } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MENU = [
  { path: "/account", label: "Profile", icon: User },
  { path: "/account/orders", label: "My Orders", icon: Package },
  { path: "/account/addresses", label: "Addresses", icon: MapPin },
  { path: "/account/wishlist", label: "Wishlist", icon: Heart },
];

function ProfilePage() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: user?.name || "", phone: user?.phone || "" });
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/user/profile`, form, { withCredentials: true });
      toast.success("Profile updated!");
    } catch {} finally { setSaving(false); }
  };

  return (
    <div data-testid="profile-page">
      <h2 className="text-xl font-bold text-slate-800 mb-6">My Profile</h2>
      <div className="bg-white rounded-2xl p-6 shadow-[0_8px_30px_rgb(0,0,0,0.04)] max-w-lg space-y-4">
        <div className="flex items-center gap-4 mb-4">
          {user?.picture ? <img src={user.picture} alt="" className="w-16 h-16 rounded-full object-cover" /> : <div className="w-16 h-16 rounded-full bg-cyan-100 flex items-center justify-center text-cyan-600 text-2xl font-bold">{user?.name?.[0]}</div>}
          <div><p className="font-semibold text-slate-800">{user?.name}</p><p className="text-sm text-slate-500">{user?.email}</p><Badge className="mt-1 bg-cyan-50 text-cyan-700 text-xs">{user?.role}</Badge></div>
        </div>
        <div><Label className="text-xs">Name</Label><Input value={form.name} onChange={e => setForm({...form, name: e.target.value})} className="rounded-xl mt-1" data-testid="profile-name-input" /></div>
        <div><Label className="text-xs">Phone</Label><Input value={form.phone} onChange={e => setForm({...form, phone: e.target.value})} className="rounded-xl mt-1" data-testid="profile-phone-input" /></div>
        <div><Label className="text-xs">Email</Label><Input value={user?.email || ""} disabled className="rounded-xl mt-1 bg-slate-50" /></div>
        <div className="flex gap-2">
          <Button onClick={save} disabled={saving} className="rounded-full bg-cyan-600 hover:bg-cyan-700" data-testid="save-profile-btn">{saving ? "Saving..." : "Save Changes"}</Button>
          <Button onClick={() => { logout(); navigate("/"); }} variant="outline" className="rounded-full text-red-500 border-red-200"><LogOut className="w-4 h-4 mr-1" /> Logout</Button>
        </div>
      </div>
    </div>
  );
}

function OrdersPage() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => { axios.get(`${API}/orders`, { withCredentials: true }).then(r => setOrders(r.data)).finally(() => setLoading(false)); }, []);

  const statusColor = { placed: "bg-blue-100 text-blue-700", confirmed: "bg-cyan-100 text-cyan-700", packed: "bg-indigo-100 text-indigo-700", shipped: "bg-amber-100 text-amber-700", delivered: "bg-emerald-100 text-emerald-700", cancelled: "bg-red-100 text-red-700" };

  if (loading) return <div className="space-y-4">{[1,2,3].map(i => <div key={i} className="h-24 skeleton-loading rounded-2xl" />)}</div>;

  return (
    <div data-testid="orders-page">
      <h2 className="text-xl font-bold text-slate-800 mb-6">My Orders</h2>
      {orders.length === 0 ? <div className="text-center py-12 text-slate-500"><Package className="w-12 h-12 mx-auto mb-3 text-slate-300" /><p>No orders yet</p><Link to="/products"><Button className="mt-3 rounded-full bg-cyan-600">Start Shopping</Button></Link></div> : (
        <div className="space-y-4">
          {orders.map(o => (
            <Link key={o.order_id} to={`/order-confirmation/${o.order_id}`} className="block bg-white rounded-2xl p-4 shadow-[0_8px_30px_rgb(0,0,0,0.04)] border border-slate-100 hover:shadow-lg transition-all" data-testid={`order-${o.order_id}`}>
              <div className="flex items-center justify-between mb-3">
                <div><p className="text-sm font-semibold text-slate-800">{o.order_id}</p><p className="text-xs text-slate-500">{new Date(o.created_at).toLocaleDateString()}</p></div>
                <Badge className={`text-xs capitalize ${statusColor[o.status] || 'bg-slate-100 text-slate-700'}`}>{o.status}</Badge>
              </div>
              <div className="flex items-center gap-2">
                {o.items?.slice(0, 3).map((item, i) => <img key={i} src={item.image} alt="" className="w-10 h-10 rounded-lg object-cover bg-slate-50" />)}
                {o.items?.length > 3 && <span className="text-xs text-slate-500">+{o.items.length - 3} more</span>}
                <span className="ml-auto text-sm font-bold text-slate-800">Rs.{o.total}</span>
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}

function AddressesPage() {
  const [addresses, setAddresses] = useState([]);
  useEffect(() => { axios.get(`${API}/user/addresses`, { withCredentials: true }).then(r => setAddresses(r.data)); }, []);

  const deleteAddr = async (id) => {
    await axios.delete(`${API}/user/addresses/${id}`, { withCredentials: true });
    setAddresses(addresses.filter(a => a.address_id !== id));
    toast.success("Address deleted");
  };

  return (
    <div data-testid="addresses-page">
      <h2 className="text-xl font-bold text-slate-800 mb-6">My Addresses</h2>
      <div className="space-y-3">
        {addresses.map(a => (
          <div key={a.address_id} className="bg-white rounded-2xl p-4 shadow-[0_8px_30px_rgb(0,0,0,0.04)] flex items-start justify-between">
            <div><p className="text-sm font-semibold text-slate-800">{a.name} - {a.phone}</p><p className="text-xs text-slate-500">{a.address_line1}, {a.city}, {a.state} - {a.pincode}</p>{a.is_default && <Badge className="mt-1 bg-cyan-50 text-cyan-700 text-xs">Default</Badge>}</div>
            <button onClick={() => deleteAddr(a.address_id)} className="text-slate-400 hover:text-red-500"><Trash2 className="w-4 h-4" /></button>
          </div>
        ))}
        {addresses.length === 0 && <p className="text-sm text-slate-500 text-center py-8">No saved addresses. Add one at checkout.</p>}
      </div>
    </div>
  );
}

function WishlistPage() {
  const [wishlist, setWishlist] = useState({ products: [] });
  const { fetchCartCount } = useAuth();
  useEffect(() => { axios.get(`${API}/user/wishlist`, { withCredentials: true }).then(r => setWishlist(r.data)); }, []);

  const addToCart = async (pid) => {
    try {
      await axios.post(`${API}/cart`, { product_id: pid, quantity: 1 }, { withCredentials: true });
      fetchCartCount();
      toast.success("Added to cart!");
    } catch {}
  };

  const removeWishlist = async (pid) => {
    await axios.post(`${API}/user/wishlist/${pid}`, {}, { withCredentials: true });
    setWishlist({ ...wishlist, products: wishlist.products.filter(p => p.product_id !== pid) });
  };

  return (
    <div data-testid="wishlist-page">
      <h2 className="text-xl font-bold text-slate-800 mb-6">My Wishlist</h2>
      {wishlist.products?.length === 0 ? <div className="text-center py-12 text-slate-500"><Heart className="w-12 h-12 mx-auto mb-3 text-slate-300" /><p>Your wishlist is empty</p></div> : (
        <div className="space-y-3">
          {wishlist.products?.map(p => (
            <div key={p.product_id} className="bg-white rounded-2xl p-4 shadow-[0_8px_30px_rgb(0,0,0,0.04)] flex items-center gap-4">
              <Link to={`/products/${p.product_id}`}><img src={p.images?.[0] || ""} alt={p.name} className="w-16 h-16 rounded-xl object-cover bg-slate-50" /></Link>
              <div className="flex-1"><Link to={`/products/${p.product_id}`} className="text-sm font-semibold text-slate-800 hover:text-cyan-600">{p.name}</Link><p className="text-sm font-bold text-cyan-600">Rs.{p.sale_price}</p></div>
              <Button onClick={() => addToCart(p.product_id)} size="sm" className="rounded-full bg-cyan-600 hover:bg-cyan-700"><ShoppingCart className="w-3 h-3 mr-1" /> Add</Button>
              <button onClick={() => removeWishlist(p.product_id)} className="text-slate-400 hover:text-red-500"><Trash2 className="w-4 h-4" /></button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function UserAccount() {
  const location = useLocation();

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6" data-testid="user-account-page">
      <div className="flex gap-6">
        <aside className="hidden lg:block w-56 shrink-0">
          <nav className="bg-white rounded-2xl p-3 shadow-[0_8px_30px_rgb(0,0,0,0.04)] space-y-1 sticky top-24">
            {MENU.map(m => (
              <Link key={m.path} to={m.path} className={`flex items-center gap-2 px-3 py-2.5 rounded-xl text-sm transition-colors ${location.pathname === m.path ? 'bg-cyan-50 text-cyan-700 font-medium' : 'text-slate-600 hover:bg-slate-50'}`}>
                <m.icon className="w-4 h-4" /> {m.label}
              </Link>
            ))}
          </nav>
        </aside>
        <div className="flex-1 min-w-0">
          <Routes>
            <Route index element={<ProfilePage />} />
            <Route path="orders" element={<OrdersPage />} />
            <Route path="addresses" element={<AddressesPage />} />
            <Route path="wishlist" element={<WishlistPage />} />
          </Routes>
        </div>
      </div>
    </div>
  );
}
